import { motion, AnimatePresence } from 'framer-motion';
import type { FilaJukebox } from '@/shared/types';
import { getClientePrimeiroNome } from '@/features/jukebox';

const MAX_VISIVEIS = 3;
const TEXT_SHADOW = '0 1px 8px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,1)';

interface Props {
  itens:   FilaJukebox[];
  visivel: boolean;
}

export default function TvFilaProximaTicker({ itens, visivel }: Props) {
  const filtrados = itens.filter(i => !i.is_invasao_tematica);
  const preview   = filtrados.slice(0, MAX_VISIVEIS);
  const extras    = filtrados.length - preview.length;

  return (
    <AnimatePresence>
      {visivel && preview.length > 0 && (
        <motion.div
          className="fixed left-6 bottom-[clamp(88px,9vw,150px)] z-[8] pointer-events-none max-w-[min(42vw,520px)]"
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          aria-live="polite"
        >
          <p
            className="text-orange-400 font-black uppercase tracking-[0.2em] leading-none mb-[0.45vw] text-[clamp(8px,0.58vw,12px)]"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Próximo na fila
          </p>

          <div className="flex flex-col items-start gap-[0.35vw]">
            {preview.map((item, idx) => {
              const nome = getClientePrimeiroNome(item.cliente_nome);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.25 }}
                  className="flex items-center gap-[0.5vw] max-w-full"
                >
                  <div className="relative shrink-0">
                    <span
                      className="absolute -top-1 -right-1 flex h-[clamp(14px,1vw,18px)] w-[clamp(14px,1vw,18px)] items-center justify-center rounded-full bg-orange-500 text-[clamp(7px,0.45vw,9px)] font-black text-white shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
                      style={{ textShadow: 'none' }}
                    >
                      {idx + 1}
                    </span>
                    <div className="rounded-lg overflow-hidden ring-1 ring-white/30 w-[clamp(32px,2.5vw,46px)] h-[clamp(32px,2.5vw,46px)] shadow-[0_4px_16px_rgba(0,0,0,0.55)]">
                      <img
                        src={item.thumbnail}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const t = e.target as HTMLImageElement;
                          t.style.display = 'none';
                          if (t.parentElement) {
                            t.parentElement.style.background = 'linear-gradient(135deg,#1e293b,#0f172a)';
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="min-w-0">
                    {nome && (
                      <p
                        className="text-orange-300/90 font-semibold uppercase tracking-wider leading-none mb-0.5 text-[clamp(7px,0.48vw,10px)] truncate"
                        style={{ textShadow: TEXT_SHADOW }}
                      >
                        {nome}
                      </p>
                    )}
                    <p
                      className="text-white font-bold leading-tight line-clamp-1 text-[clamp(10px,0.82vw,16px)]"
                      style={{ textShadow: TEXT_SHADOW }}
                    >
                      {item.titulo}
                    </p>
                  </div>
                </motion.div>
              );
            })}

            {extras > 0 && (
              <span
                className="mt-[0.15vw] inline-flex items-center rounded-full bg-orange-500/90 px-[0.55vw] py-[0.2vw] text-[clamp(8px,0.55vw,11px)] font-black text-white shadow-[0_2px_10px_rgba(0,0,0,0.45)]"
                style={{ textShadow: 'none' }}
              >
                +{extras}
              </span>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
